import { memo, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { AnimatePresence } from 'framer-motion'

import * as Styled from './styled'
import { TProps } from './types'
import { useRelativeCoords } from 'hooks/useRelativeCoords'
import { IOptionsType } from 'hooks/useRelativeCoords'
import { ANIMATION_PER_POSITION } from './consts'
import { useOnClickOutside } from 'hooks/useOnClickOutside'

const Content = ({
  onClose,
  renderingContainer,
  anchorEl,
  className,
  children,
  options,
  ignoredSelectors,
  ...rest
}: TProps) => {
  const flyoutRef = useRef<HTMLDivElement | null>(null)
  const flyoutOptions: IOptionsType = {
    position: 'top',
    alignmentY: 'top',
    alignmentX: 'right',
    extraOffset: 5,
    ...(options || {})
  }
  const coords = useRelativeCoords({
    container: renderingContainer,
    anchorEl,
    targetRef: flyoutRef,
    options: flyoutOptions
  })

  const prevCoords = useRef({ top: 0, left: 0 })
  const [calculatedCoords, setCalculatedCoords] = useState(coords)

  // Check if position coordinates (top/left) have changed after render in case of children element height changes
  const isPositionCoordsChangedAfterRender = useMemo(
    () =>
      prevCoords.current.top !== calculatedCoords.top ||
      prevCoords.current.left !== calculatedCoords.left,
    [calculatedCoords, prevCoords]
  )

  useLayoutEffect(() => {
    // Update previous coordinates when coords change
    requestAnimationFrame(() => {
      setCalculatedCoords(prev => {
        prevCoords.current = prev
        return coords
      })
    })
  }, [coords])

  useOnClickOutside([flyoutRef?.current, anchorEl], onClose, ignoredSelectors)

  return (
    <Styled.FlyoutWrapper
      $coords={coords}
      $isPositionCoordsChangedAfterRender={isPositionCoordsChangedAfterRender}
      className={className}
      {...ANIMATION_PER_POSITION[flyoutOptions.position]}
      {...rest}
    >
      <div
        ref={flyoutRef}
        className={className ? `${className}_container` : ''}
      >
        {children}
      </div>
    </Styled.FlyoutWrapper>
  )
}

const Flyout = (props: TProps) => {
  const { isOpen } = props
  return ReactDOM.createPortal(
    <AnimatePresence>{isOpen ? <Content {...props} /> : null}</AnimatePresence>,
    props.renderingContainer || document.body
  )
}

export default memo(Flyout)
export type { TProps as TFlyoutProps }
